import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { syncSearchParams } from '../lib/urlSync';
import { getCached, swrFetch, invalidatePrefix } from '../lib/dataCache';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from '../components/ui/command';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { tasksAPI, usersAPI, prospectsAPI, teamsV2API } from '../lib/api';
import { INTERNAL_THEMES } from '../lib/internalThemes';
import { NewTaskDialog } from '../components/tasks/NewTaskDialog';
import { FilRougeFrequencyFields, filRougeRhythm } from '../components/tasks/FilRougeFrequencyFields';
import { UserPickerPopover } from '../components/common/UserPickerPopover';
import {
    Plus, Search, Clock, AlertTriangle, CheckCircle, CheckCircle2,
    Circle, Calendar, User, Trash2, Edit, Loader2, ListTodo,
    ArrowUp, ArrowRight, ArrowDown, Zap, Bell, Tag
} from 'lucide-react';
import { cn } from '../lib/utils';

function getPriorityConfig(priority) {
    if (priority === 'low') return { label: 'Basse', color: 'bg-slate-100 text-slate-600', Icon: ArrowDown };
    if (priority === 'medium') return { label: 'Moyenne', color: 'bg-blue-100 text-blue-700', Icon: ArrowRight };
    if (priority === 'high') return { label: 'Haute', color: 'bg-orange-100 text-orange-700', Icon: ArrowUp };
    if (priority === 'urgent') return { label: 'Urgente', color: 'bg-red-100 text-red-700', Icon: Zap };
    return { label: priority, color: 'bg-slate-100 text-slate-600', Icon: ArrowRight };
}

function formatDate(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
}

function isOverdue(dateStr, status, taskFlag) {
    // ⚠️ SOURCE DE VÉRITÉ = backend. Chaque tâche renvoyée par /api/tasks
    // porte `is_overdue` déjà calculé serveur (même règle que la cloche et
    // stats/summary → cohérence garantie). Si le flag est présent on l'utilise
    // tel quel, sinon fallback local (cas d'une tâche fraîchement créée non
    // encore renvoyée par la liste).
    // 🩹 FIX 10-02-2026 : les tâches `postponed` dont la nouvelle date est
    // dépassée doivent AUSSI apparaître en retard (avant : elles étaient
    // silencieusement exclues).
    if (typeof taskFlag === 'boolean') return taskFlag;
    if (!dateStr || status === 'done') return false;
    const due = new Date(dateStr);
    if (isNaN(due.getTime())) return false;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return due.getTime() < startOfToday.getTime();
}

function daysOverdue(dateStr, status) {
    if (!isOverdue(dateStr, status, null)) return 0;
    const due = new Date(dateStr);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((startOfToday - due) / 86400000));
}

// Plage de dates pour le filtre période
function periodRange(period) {
    const now = new Date();
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    if (period === 'today') return [start, new Date(start.getTime() + 86400000)];
    if (period === 'week') {
        const dow = start.getDay() || 7; // lundi = 1
        const ws = new Date(start); ws.setDate(start.getDate() - (dow - 1));
        const we = new Date(ws); we.setDate(ws.getDate() + 7);
        return [ws, we];
    }
    if (period === 'month') {
        return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 1)];
    }
    return null;
}

function taskUsers(task) {
    // Ensemble des utilisateurs ASSIGNÉS uniquement (jamais le créateur).
    // Le filtre "Moi (…)" doit compter les tâches dont je suis assigné, PAS celles que j'ai créées.
    const ids = new Set([...(task.assignee_ids || [])]);
    if (task.assigned_to) ids.add(task.assigned_to);
    return ids;
}

export default function Tasks() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [prospectsLoaded, setProspectsLoaded] = useState(false);
    const [prospectPopoverOpen, setProspectPopoverOpen] = useState(false);
    const [prospectSearch, setProspectSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newTaskDialogOpen, setNewTaskDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [saving, setSaving] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    // Stats agrégées côté serveur (count_documents) — même source que la cloche.
    // Utilisées pour les cartes quand aucun filtre n'est actif → zéro dérive
    // possible entre "EN RETARD" du Pilotage et badge de la cloche.
    const [serverStats, setServerStats] = useState(null);
    // Prolongation de tâche (raison + nouvelle date plus lointaine)
    const [postponeTask, setPostponeTask] = useState(null);
    const [postponeReason, setPostponeReason] = useState('');
    const [postponeDate, setPostponeDate] = useState('');
    const [completeTask, setCompleteTask] = useState(null);
    const [completeComment, setCompleteComment] = useState('');

    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isAdminFull = user?.role === 'admin_full' || user?.is_admin === true;
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';

    // Tri par échéance + sélection multiple (Full Admin)
    const [sortDir, setSortDir] = useState('asc'); // asc = échéance la plus proche
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Filtres (préservés dans l'URL)
    const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all'); // all|todo|in_progress|done|overdue
    const [filterPriority, setFilterPriority] = useState(searchParams.get('priority') || 'all');
    const [filterPeriod, setFilterPeriod] = useState(searchParams.get('period') || 'all'); // all|today|week|month
    const [filterTeam, setFilterTeam] = useState(searchParams.get('team') || ''); // admin only
    const [filterUser, setFilterUser] = useState(searchParams.get('member') || ''); // admin & vp
    const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');

    useEffect(() => {
        const params = new URLSearchParams();
        if (filterStatus !== 'all') params.set('status', filterStatus);
        if (filterPriority !== 'all') params.set('priority', filterPriority);
        if (filterPeriod !== 'all') params.set('period', filterPeriod);
        if (filterTeam) params.set('team', filterTeam);
        if (filterUser) params.set('member', filterUser);
        if (searchTerm) params.set('q', searchTerm);
        syncSearchParams(setSearchParams, searchParams, params);
    }, [filterStatus, filterPriority, filterPeriod, filterTeam, filterUser, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    const [form, setForm] = useState({
        priority: 'medium', due_date: '',
        assigned_to: '', assignee_ids: [], prospect_id: '', module: 'both', category: 'dossier', theme: '', is_team_task: false,
        // Fil rouge (Admin/VP) — tâche récurrente quotidienne
        is_fil_rouge: false, fil_rouge_start: '', fil_rouge_days: 7, fil_rouge_time: '09:00', fil_rouge_frequency: 'daily', fil_rouge_weekday: 0, target_scope: 'custom'
    });
    // Refs pour les inputs TEXTE (non-contrôlés → 0 re-render à la frappe).
    // Le lag mobile venait du re-render complet du Dialog (Selects × 4, 188+
    // prospects, participants…) à CHAQUE touche. Refs = frappe locale, lecture
    // au submit.
    const titleRef = useRef(null);
    const descriptionRef = useRef(null);
    // Recherche dossier — pattern identique à CalendarPage (aucun résultat par défaut,
    // filtre à partir de 1 caractère, max 25 résultats). La visibilité est déjà
    // gérée côté backend par /prospects/picker selon le rôle.
    const filteredProspects = useMemo(() => {
        if (!prospectSearch || prospectSearch.trim().length < 1) return [];
        const search = prospectSearch.toLowerCase();
        return prospects.filter(p =>
            (p.company_name?.toLowerCase().includes(search)) ||
            (p.first_name?.toLowerCase().includes(search)) ||
            (p.last_name?.toLowerCase().includes(search)) ||
            (p.label?.toLowerCase().includes(search))
        ).slice(0, 25);
    }, [prospects, prospectSearch]);
    const selectedProspect = useMemo(() => {
        if (!form.prospect_id) return null;
        return prospects.find(p => p.id === form.prospect_id);
    }, [form.prospect_id, prospects]);
    // Compteur remonté à chaque ouverture pour vider les inputs non-contrôlés
    // (defaultValue ne re-synchronise pas quand le state change).
    const [formKey, setFormKey] = useState(0);

    // Utilisateurs assignables / filtrables selon le rôle
    const assignableUsers = useMemo(() => {
        if (isAdmin) return users.filter(u => u.is_active !== false);
        if (isVP) {
            const list = [user];
            users.forEach(u => {
                if (u.id === user.id || u.is_active === false) return;
                if (u.role === 'admin_full' || u.role === 'admin') { list.push(u); return; }
                if (u.vp_id === user.id) { list.push(u); return; }
            });
            teamMembers.forEach(m => { if (!list.find(a => a.id === m.id)) list.push(m); });
            return list;
        }
        if (isApporteur) {
            const list = [user];
            users.forEach(u => {
                if (u.id === user.id || u.is_active === false) return;
                if (u.role === 'admin_full' || u.role === 'admin') { list.push(u); return; }
                if (u.id === user.vp_id) { list.push(u); return; }
                if (u.role === 'apporteur' && u.vp_id === user.vp_id && user.vp_id) { list.push(u); return; }
            });
            return list;
        }
        return [];
    }, [users, user, isAdmin, isVP, isApporteur, teamMembers]);

    const loadData = useCallback(async () => {
        const params = {};
        if (isVP) params.team_scope = 'true'; // self + équipe
        // admin: {} = toutes ; apporteur: {} = les siennes (scopé backend)

        const key = `tasks:${user?.id}:${user?.role}`;
        const cached = getCached(key);
        if (cached) {
            setTasks(cached.tasks); setUsers(cached.users); setTeams(cached.teams || []);
            if (cached.teamMembers) setTeamMembers(cached.teamMembers);
            setLoading(false);
        } else { setLoading(true); }
        try {
            const data = await swrFetch(key, async () => {
                const [tasksRes, usersRes, teamsRes, vpExtra] = await Promise.all([
                    tasksAPI.getAll(params),
                    usersAPI.getAll().catch(() => ({ data: [user] })),
                    isAdmin ? teamsV2API.getAll().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                    isVP ? usersAPI.getAssignableUsers(user.id).catch(() => ({ data: [] })) : Promise.resolve(null),
                ]);
                return {
                    tasks: tasksRes.data || [],
                    users: usersRes.data || [user],
                    teams: teamsRes.data || [],
                    teamMembers: vpExtra ? (vpExtra.data || []) : undefined,
                };
            });
            setTasks(data.tasks); setUsers(data.users); setTeams(data.teams || []);
            if (data.teamMembers) setTeamMembers(data.teamMembers);
        } catch (err) {
            if (!cached) toast.error('Erreur lors du chargement');
        } finally { setLoading(false); }
    }, [isAdmin, isVP, user]);

    useEffect(() => { loadData(); }, [loadData]);

    // Refresh périodique des stats serveur (mêmes count_documents que la cloche)
    // → cartes du Pilotage toujours alignées avec le badge notif, même quand
    //   des tâches franchissent leur deadline pendant la session.
    useEffect(() => {
        let alive = true;
        const fetchStats = async () => {
            try {
                const r = await tasksAPI.getStats();
                if (alive) setServerStats(r.data || null);
            } catch { /* silencieux — fallback sur counts locaux */ }
        };
        fetchStats();
        const t = setInterval(fetchStats, 30000);
        return () => { alive = false; clearInterval(t); };
    }, [user?.id]);

    // Charger les prospects uniquement à l'ouverture du formulaire (allège la page)
    // ⚠️ MOBILE PERF (27/02/2026) : utilise `getPicker` (payload ultra-léger {id,label}
    // par dossier au lieu du prospect complet) pour éviter le lag à l'ouverture de
    // la modale sur mobile. Préchargé au mount ci-dessous pour être instantané.
    const ensureProspects = useCallback(async () => {
        if (prospectsLoaded) return;
        try {
            const res = await prospectsAPI.getPicker();
            setProspects(res.data || []);
            setProspectsLoaded(true);
        } catch { /* ignore */ }
    }, [prospectsLoaded]);

    // Précharge silencieusement dès le mount → ouverture modale = instantanée
    useEffect(() => { ensureProspects(); }, [ensureProspects]);

    // Membres d'une équipe (admin) pour le filtre équipe
    const teamMemberIds = useMemo(() => {
        if (!filterTeam) return null;
        const t = teams.find(x => x.id === filterTeam);
        return t ? new Set(t.member_ids || []) : new Set();
    }, [filterTeam, teams]);

    // baseFiltered = tout SAUF le statut (les cartes comptent là-dessus)
    const baseFiltered = useMemo(() => {
        const range = periodRange(filterPeriod);
        return tasks.filter(task => {
            if (searchTerm) {
                const q = searchTerm.toLowerCase();
                if (!(task.title?.toLowerCase().includes(q) || task.description?.toLowerCase().includes(q) || task.prospect_name?.toLowerCase().includes(q))) return false;
            }
            if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
            if (range) {
                const ref = new Date(task.due_date || task.created_at);
                if (!(ref >= range[0] && ref < range[1])) return false;
            }
            if (filterTeam && teamMemberIds) {
                const tu = taskUsers(task);
                if (![...teamMemberIds].some(id => tu.has(id))) return false;
            }
            if (filterUser) {
                if (!taskUsers(task).has(filterUser)) return false;
            }
            return true;
        });
    }, [tasks, searchTerm, filterPriority, filterPeriod, filterTeam, teamMemberIds, filterUser]);

    // Une tâche "active" = à faire ou en cours (in_progress affiché comme "à faire")
    const isActiveStatus = (t) => t.status === 'todo' || t.status === 'in_progress';
    // Aucun filtre actif = les cartes reflètent la DB complète (via stats serveur)
    const noFilterActive = (
        filterPriority === 'all'
        && filterPeriod === 'all'
        && !filterTeam
        && !filterUser
        && !searchTerm
    );
    // Buckets MUTUELLEMENT EXCLUSIFS : à faire + en retard + prolongées + terminées = total
    // Priorité aux stats serveur (source unique avec la cloche) quand aucun filtre
    // n'est actif. Sinon on retombe sur le calcul local (via flag serveur is_overdue).
    const counts = useMemo(() => {
        const local = {
            total: baseFiltered.length,
            todo: baseFiltered.filter(t => isActiveStatus(t) && !isOverdue(t.due_date, t.status, t.is_overdue)).length,
            // 🩹 FIX 10-02-2026 : une tâche prolongée dont la nouvelle date est dépassée
            // sort du bucket "Prolongées" et bascule dans "En retard" (buckets mutuellement
            // exclusifs).
            postponed: baseFiltered.filter(t => t.status === 'postponed' && !isOverdue(t.due_date, t.status, t.is_overdue)).length,
            done: baseFiltered.filter(t => t.status === 'done').length,
            overdue: baseFiltered.filter(t => isOverdue(t.due_date, t.status, t.is_overdue)).length,
        };
        if (noFilterActive && serverStats) {
            // Reste cohérent avec la cloche : mêmes count_documents, même règle.
            // Carte "À faire" = actives (todo + in_progress) MOINS celles en retard,
            // car les buckets sont mutuellement exclusifs côté UI.
            const active = (serverStats.todo || 0) + (serverStats.in_progress || 0);
            const overdue = serverStats.overdue || 0;
            return {
                total: serverStats.total ?? local.total,
                todo: Math.max(0, active - overdue),
                postponed: serverStats.postponed ?? local.postponed,
                done: serverStats.done ?? local.done,
                overdue,
            };
        }
        return local;
    }, [baseFiltered, noFilterActive, serverStats]);

    const displayedTasks = useMemo(() => {
        let list;
        if (filterStatus === 'all') list = baseFiltered;
        else if (filterStatus === 'overdue') list = baseFiltered.filter(t => isOverdue(t.due_date, t.status, t.is_overdue));
        else if (filterStatus === 'todo') list = baseFiltered.filter(t => isActiveStatus(t) && !isOverdue(t.due_date, t.status, t.is_overdue));
        else if (filterStatus === 'postponed') list = baseFiltered.filter(t => t.status === 'postponed' && !isOverdue(t.due_date, t.status, t.is_overdue));
        else list = baseFiltered.filter(t => t.status === filterStatus);
        // Tri par échéance (les tâches sans échéance vont à la fin)
        return [...list].sort((a, b) => {
            const av = a.due_date || '', bv = b.due_date || '';
            if (!av && !bv) return 0;
            if (!av) return 1;
            if (!bv) return -1;
            return sortDir === 'asc' ? (av < bv ? -1 : 1) : (av < bv ? 1 : -1);
        });
    }, [baseFiltered, filterStatus, sortDir]);

    // Deep-link : surligner + scroller vers une tâche précise (depuis une notification).
    // Robuste : fonctionne à l'arrivée sur la page ET si on y est déjà (clic notif).
    const location = useLocation();
    const [targetTaskId, setTargetTaskId] = useState(null);
    const [flashTaskId, setFlashTaskId] = useState(null);
    // Deep-link `?edit=<id>` : ouvre directement la modale d'édition de la tâche
    // (brief Uatu fév. 2026 — les liens du chat doivent atterrir sur LA tâche
    // prête à être modifiée, pas sur la liste générale).
    const [targetEditId, setTargetEditId] = useState(null);
    useEffect(() => {
        let hid = null;
        let eid = null;
        try {
            const sp = new URLSearchParams(location.search);
            hid = sp.get('highlight');
            eid = sp.get('edit');
        } catch { hid = null; eid = null; }
        if (eid) {
            // Neutralise les filtres pour être sûr de trouver la tâche en base
            setFilterStatus('all'); setFilterPriority('all'); setFilterPeriod('all');
            setFilterTeam(''); setFilterUser(''); setSearchTerm('');
            setTargetEditId(eid);
            return;
        }
        if (!hid) return;
        // Filtres neutres pour garantir que la tâche ciblée soit visible (Terminée, autre équipe…)
        setFilterStatus('all'); setFilterPriority('all'); setFilterPeriod('all');
        setFilterTeam(''); setFilterUser(''); setSearchTerm('');
        setTargetTaskId(hid);
    }, [location.search]);
    useEffect(() => {
        if (!targetTaskId || loading) return;
        if (!displayedTasks.some((t) => t.id === targetTaskId)) return;
        setFlashTaskId(targetTaskId);
        const el = document.querySelector(`[data-testid="task-row-${targetTaskId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const t = setTimeout(() => { setFlashTaskId(null); setTargetTaskId(null); }, 4500);
        return () => clearTimeout(t);
    }, [targetTaskId, loading, displayedTasks]);
    // Auto-ouverture modale d'édition dès que la tâche cible est chargée.
    // ROBUSTE : si la tâche n'apparaît pas dans le state local (ex. filtrée
    // côté serveur, permission d'affichage limitée…), on la récupère via
    // `tasksAPI.getById()` pour ouvrir la modale quand même.
    useEffect(() => {
        if (!targetEditId || loading) return;
        const task = displayedTasks.find((t) => t.id === targetEditId) || tasks.find((t) => t.id === targetEditId);
        const openAndCleanup = (t) => {
            openEdit(t);
            setTargetEditId(null);
            try {
                const url = new URL(window.location.href);
                url.searchParams.delete('edit');
                window.history.replaceState({}, '', url.toString());
            } catch { /* noop */ }
        };
        if (task) {
            openAndCleanup(task);
            return;
        }
        // Fallback : la tâche n'est pas dans la liste chargée → fetch direct
        let cancelled = false;
        (async () => {
            try {
                const r = await tasksAPI.getById(targetEditId);
                if (!cancelled && r?.data?.id) openAndCleanup(r.data);
                else if (!cancelled) {
                    toast.error("Cette tâche n'existe plus ou vous n'y avez pas accès.");
                    setTargetEditId(null);
                }
            } catch (e) {
                if (!cancelled) {
                    const s = e?.response?.status;
                    if (s === 404) toast.error("Cette tâche n'existe plus.");
                    else if (s === 403) toast.error("Vous n'avez pas accès à cette tâche.");
                    else toast.error("Impossible d'ouvrir la tâche.");
                    setTargetEditId(null);
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetEditId, loading, displayedTasks, tasks]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Lecture des champs texte depuis les refs (inputs non-contrôlés)
        const title = (titleRef.current?.value || '').trim();
        const description = descriptionRef.current?.value || '';
        if (!title) { toast.error('Le titre est obligatoire'); return; }
        if (form.category === 'dossier') {
            if (!form.prospect_id) { toast.error('Une tâche « dossier » doit être liée à un prospect'); return; }
            if (!form.module) { toast.error('Choisissez le module (Courtage ou Travaux d’optimisation)'); return; }
        } else if (!form.theme) {
            toast.error('Choisissez un thème pour la tâche interne');
            return;
        }

        // ---- Mode Fil rouge (création uniquement) ----
        if (form.is_fil_rouge && !editingTask) {
            if (!form.fil_rouge_start) { toast.error('Choisissez une date de début pour le fil rouge'); return; }
            const days = Number(form.fil_rouge_days);
            if (!Number.isInteger(days) || days < 1 || days > 60) { toast.error('Le nombre de jours doit être entre 1 et 60'); return; }
            if (form.target_scope === 'custom' && (form.assignee_ids || []).length === 0) {
                toast.error('Sélectionnez au moins une personne (ou choisissez « Tout le monde » / « Mon équipe »)'); return;
            }
            setSaving(true);
            try {
                const res = await tasksAPI.createFilRouge({
                    title, description, priority: form.priority,
                    start_date: form.fil_rouge_start, days, daily_time: form.fil_rouge_time,
                    frequency: form.fil_rouge_frequency,
                    weekday: form.fil_rouge_frequency === 'weekly' ? form.fil_rouge_weekday : null,
                    category: form.category, prospect_id: form.category === 'dossier' ? form.prospect_id : null,
                    module: form.category === 'dossier' ? form.module : null,
                    theme: form.category === 'interne' ? form.theme : null,
                    target_scope: form.target_scope,
                    assignee_ids: form.target_scope === 'custom' ? form.assignee_ids : [],
                });
                toast.success(`Fil rouge créé : ${res.data.occurrences} occurrence(s) sur ${res.data.days} jour(s) pour ${res.data.people} personne(s)`);
                setDialogOpen(false); resetForm();
                invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
            } catch (err) {
                const detail = err.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Erreur lors de la création du fil rouge'));
            } finally { setSaving(false); }
            return;
        }

        setSaving(true);
        try {
            const payload = { ...form, title, description };
            if (!Array.isArray(payload.assignee_ids) || payload.assignee_ids.length === 0) {
                payload.assignee_ids = payload.assigned_to ? [payload.assigned_to] : [];
            }
            payload.assigned_to = payload.assignee_ids[0] || '';
            if (editingTask) { await tasksAPI.update(editingTask.id, payload); toast.success('Tâche mise à jour'); }
            else { await tasksAPI.create(payload); toast.success('Tâche créée'); }
            setDialogOpen(false); resetForm();
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally { setSaving(false); }
    };

    const handleStatusChange = async (taskId, newStatus) => {
        if (newStatus === 'postponed') {
            // Ouvrir le dialog de prolongation (raison + nouvelle date plus lointaine obligatoires)
            const task = tasks.find(t => t.id === taskId);
            setPostponeTask(task || { id: taskId });
            setPostponeReason('');
            setPostponeDate('');
            return;
        }
        if (newStatus === 'done') {
            const task = tasks.find(t => t.id === taskId);
            // Commentaire obligatoire pour terminer une tâche DOSSIER (hors fil rouge / interne)
            const requiresComment = task && task.category !== 'interne' && !task.is_fil_rouge && task.prospect_id;
            if (requiresComment) {
                setCompleteTask(task);
                setCompleteComment('');
                return;
            }
        }
        try {
            await tasksAPI.update(taskId, { status: newStatus });
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch { toast.error('Erreur'); }
    };

    const submitComplete = async () => {
        if (!completeComment.trim()) { toast.error('Indiquez ce qui a été fait pour terminer la tâche'); return; }
        try {
            setSaving(true);
            await tasksAPI.update(completeTask.id, { status: 'done', completion_comment: completeComment.trim() });
            toast.success('Tâche terminée — compte-rendu publié sur le mur');
            setCompleteTask(null);
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la clôture');
        } finally { setSaving(false); }
    };

    const submitPostpone = async () => {
        if (!postponeReason.trim()) { toast.error('Indiquez une raison de prolongation'); return; }
        if (!postponeDate) { toast.error('Choisissez une nouvelle date (plus lointaine)'); return; }
        try {
            setSaving(true);
            await tasksAPI.update(postponeTask.id, {
                status: 'postponed',
                postpone_reason: postponeReason.trim(),
                due_date: postponeDate,
            });
            toast.success('Tâche prolongée');
            setPostponeTask(null);
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la prolongation');
        } finally { setSaving(false); }
    };

    const handleRemind = async (taskId) => {
        try {
            const res = await tasksAPI.remind(taskId);
            toast.success(`Relance envoyée à ${res.data?.count ?? ''} personne(s)`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la relance'); }
    };

    const handleDelete = async (taskId) => {
        if (!window.confirm('Supprimer cette tâche ?')) return;
        try {
            await tasksAPI.delete(taskId); toast.success('Tâche supprimée');
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch { toast.error('Erreur'); }
    };

    const toggleSelect = (id) => setSelectedIds(prev => {
        const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
    const allDisplayedSelected = displayedTasks.length > 0 && displayedTasks.every(t => selectedIds.has(t.id));
    const toggleSelectAll = () => setSelectedIds(() => allDisplayedSelected ? new Set() : new Set(displayedTasks.map(t => t.id)));
    const handleBulkDelete = async () => {
        const ids = [...selectedIds];
        if (ids.length === 0) return;
        if (!window.confirm(`Supprimer définitivement ${ids.length} tâche(s) ?`)) return;
        try {
            const res = await tasksAPI.bulkDelete(ids);
            toast.success(`${res.data?.deleted ?? ids.length} tâche(s) supprimée(s)`);
            setSelectedIds(new Set());
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const openEdit = (task) => {
        ensureProspects();
        setEditingTask(task);
        setForm({
            priority: task.priority,
            due_date: task.due_date || '', assigned_to: task.assigned_to || '',
            assignee_ids: task.assignee_ids || (task.assigned_to ? [task.assigned_to] : []),
            prospect_id: task.prospect_id || '', module: task.module || 'both',
            category: task.category || 'dossier', theme: task.theme || '',
            is_team_task: task.is_team_task || false
        });
        // Bump la clé AVANT d'ouvrir pour que les inputs non-contrôlés
        // reçoivent le bon defaultValue (title/description de la tâche à éditer).
        setFormKey(k => k + 1);
        setDialogOpen(true);
    };

    const resetForm = () => {
        setEditingTask(null);
        setForm({ priority: 'medium', due_date: '', assigned_to: '', assignee_ids: [], prospect_id: '', module: 'both', category: 'dossier', theme: '', is_team_task: false, is_fil_rouge: false, fil_rouge_start: '', fil_rouge_days: 7, fil_rouge_time: '09:00', fil_rouge_frequency: 'daily', fil_rouge_weekday: 0, target_scope: 'custom' });
        setFormKey(k => k + 1); // Vide les inputs non-contrôlés
    };

    const STATUS_CARDS = [
        { key: 'all', label: 'Total', value: counts.total, Icon: ListTodo, active: 'ring-slate-400', bg: 'bg-slate-50', text: 'text-slate-700' },
        { key: 'todo', label: 'À faire', value: counts.todo, Icon: Circle, active: 'ring-slate-400', bg: 'bg-slate-50', text: 'text-slate-700' },
        { key: 'postponed', label: 'Prolongées', value: counts.postponed, Icon: Clock, active: 'ring-amber-400', bg: 'bg-amber-50', text: 'text-amber-700' },
        { key: 'done', label: 'Terminées', value: counts.done, Icon: CheckCircle, active: 'ring-green-400', bg: 'bg-green-50', text: 'text-green-700' },
        { key: 'overdue', label: 'En retard', value: counts.overdue, Icon: AlertTriangle, active: 'ring-red-400', bg: counts.overdue > 0 ? 'bg-red-50' : 'bg-slate-50', text: counts.overdue > 0 ? 'text-red-700' : 'text-slate-700' },
    ];

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="tasks-page">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">
                            {isAdmin ? 'Pilotage des tâches' : isVP ? 'Tâches de mon équipe' : 'Mes tâches'}
                        </h1>
                        <p className="text-slate-500 mt-1">Chaque tâche est liée à un dossier · suivez qui fait quoi</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (open) ensureProspects(); else resetForm(); }}>
                        <Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-task-btn" onClick={() => setNewTaskDialogOpen(true)}>
                            <Plus className="w-4 h-4 mr-2" />Nouvelle tâche
                        </Button>
                        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto overflow-x-auto sm:w-full" key={formKey}>
                            <DialogHeader>
                                <DialogTitle>{editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Titre *</Label>
                                    <Input ref={titleRef} defaultValue={editingTask?.title || ''} placeholder="Ex: Appeler le prospect Dupont" required data-testid="task-title-input" />
                                </div>
                                {/* Nature : Dossier (commercial) ou Interne (organisationnel) */}
                                <div className="space-y-2">
                                    <Label>Nature</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button type="button" onClick={() => setForm(p => ({ ...p, category: 'dossier' }))}
                                            data-testid="task-category-dossier"
                                            className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.category === 'dossier' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                            🏢 Dossier
                                        </button>
                                        <button type="button" onClick={() => setForm(p => ({ ...p, category: 'interne' }))}
                                            data-testid="task-category-interne"
                                            className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.category === 'interne' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                            🗂️ Interne
                                        </button>
                                    </div>
                                </div>
                                {form.category === 'dossier' ? (
                                  <>
                                    {/* Prospect lié — OBLIGATOIRE (recherche as-you-type) */}
                                    <div className="space-y-2">
                                        <Label>Prospect lié *</Label>
                                        <Popover open={prospectPopoverOpen} onOpenChange={setProspectPopoverOpen}>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    data-testid="task-prospect-trigger"
                                                    className="flex h-9 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-slate-400"
                                                >
                                                    {selectedProspect ? (
                                                        <span className="truncate">{selectedProspect.label || selectedProspect.company_name || `${selectedProspect.first_name || ''} ${selectedProspect.last_name || ''}`.trim()}</span>
                                                    ) : (
                                                        <span className="text-slate-400">{prospectsLoaded ? 'Rechercher un dossier…' : 'Chargement…'}</span>
                                                    )}
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-[340px] p-0" align="start">
                                                <Command shouldFilter={false}>
                                                    <CommandInput
                                                        autoFocus
                                                        placeholder="Tapez le nom du dossier…"
                                                        value={prospectSearch}
                                                        onValueChange={setProspectSearch}
                                                        data-testid="task-prospect-search"
                                                    />
                                                    <CommandList>
                                                        {prospectSearch.trim().length < 1 ? (
                                                            <div className="px-3 py-3 text-xs text-slate-500">
                                                                Tape au moins 1 caractère pour rechercher un dossier parmi ceux auxquels tu as accès.
                                                            </div>
                                                        ) : filteredProspects.length === 0 ? (
                                                            <CommandEmpty>Aucun dossier trouvé pour « {prospectSearch} »</CommandEmpty>
                                                        ) : (
                                                            <CommandGroup>
                                                                {filteredProspects.map(p => (
                                                                    <CommandItem
                                                                        key={p.id}
                                                                        onSelect={() => {
                                                                            setForm(f => ({ ...f, prospect_id: p.id }));
                                                                            setProspectPopoverOpen(false);
                                                                            setProspectSearch('');
                                                                        }}
                                                                    >
                                                                        <Check className={cn('mr-2 h-4 w-4', form.prospect_id === p.id ? 'opacity-100' : 'opacity-0')} />
                                                                        {p.label || p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim()}
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        )}
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                        <p className="text-xs text-slate-400">Une tâche « dossier » est rattachée à un prospect.</p>
                                    </div>
                                    {/* Module — OBLIGATOIRE */}
                                    <div className="space-y-2">
                                        <Label>Mur du dossier concerné *</Label>
                                        <Select value={form.module} onValueChange={(v) => setForm(p => ({ ...p, module: v }))}>
                                            <SelectTrigger data-testid="task-module-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="both">Tout le dossier (Courtage + Travaux d’optimisation)</SelectItem>
                                                <SelectItem value="easy_power">Courtage</SelectItem>
                                                <SelectItem value="chantier">Travaux d’optimisation</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-400">La tâche et ses changements de statut apparaîtront sur ce mur.</p>
                                    </div>
                                  </>
                                ) : (
                                    /* Thème — pour une tâche interne */
                                    <div className="space-y-2">
                                        <Label>Thème *</Label>
                                        <Select value={form.theme || ''} onValueChange={(v) => setForm(p => ({ ...p, theme: v }))}>
                                            <SelectTrigger data-testid="task-theme-select"><SelectValue placeholder="Choisir un thème" /></SelectTrigger>
                                            <SelectContent>
                                                {INTERNAL_THEMES.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-400">Tâche organisationnelle, non liée à un dossier.</p>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <Textarea ref={descriptionRef} defaultValue={editingTask?.description || ''} placeholder="Détails supplémentaires..." rows={2} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Priorité</Label>
                                        <Select value={form.priority} onValueChange={(v) => setForm(p => ({ ...p, priority: v }))}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="low">Basse</SelectItem>
                                                <SelectItem value="medium">Moyenne</SelectItem>
                                                <SelectItem value="high">Haute</SelectItem>
                                                <SelectItem value="urgent">Urgente</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Date limite</Label>
                                        <Input type="date" value={form.due_date} disabled={form.is_fil_rouge} onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value }))} />
                                    </div>
                                </div>

                                {/* Fil rouge — tâche récurrente quotidienne (Admin / VP, création uniquement) */}
                                {(isAdmin || isVP) && !editingTask && (
                                    <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 space-y-3">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox" id="is_fil_rouge" checked={form.is_fil_rouge}
                                                onChange={(e) => setForm(p => ({ ...p, is_fil_rouge: e.target.checked }))}
                                                className="w-4 h-4 rounded text-rose-600" data-testid="task-fil-rouge-toggle" />
                                            <span className="text-sm font-semibold text-rose-700">🧵 Tâche fil rouge</span>
                                        </label>
                                        {form.is_fil_rouge && (
                                            <>
                                                <FilRougeFrequencyFields
                                                    frequency={form.fil_rouge_frequency}
                                                    weekday={form.fil_rouge_weekday}
                                                    onFrequencyChange={(value) => setForm(p => ({ ...p, fil_rouge_frequency: value }))}
                                                    onWeekdayChange={(value) => setForm(p => ({ ...p, fil_rouge_weekday: value }))}
                                                />
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                                    <div className="space-y-1">
                                                        <Label className="text-xs">Début</Label>
                                                        <Input type="date" value={form.fil_rouge_start} onChange={(e) => setForm(p => ({ ...p, fil_rouge_start: e.target.value }))} data-testid="fil-rouge-start" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <Label className="text-xs">Durée (jours)</Label>
                                                        <Input type="number" min={1} max={60} value={form.fil_rouge_days} onChange={(e) => setForm(p => ({ ...p, fil_rouge_days: e.target.value }))} data-testid="fil-rouge-days" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <Label className="text-xs">Échéance (Paris)</Label>
                                                        <Input type="time" value={form.fil_rouge_time} onChange={(e) => setForm(p => ({ ...p, fil_rouge_time: e.target.value }))} data-testid="fil-rouge-time" />
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Pour qui ?</Label>
                                                    <Select value={form.target_scope} onValueChange={(v) => setForm(p => ({ ...p, target_scope: v }))}>
                                                        <SelectTrigger data-testid="fil-rouge-scope"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            {isAdmin && <SelectItem value="everyone">Tout le monde</SelectItem>}
                                                            {isVP && <SelectItem value="team">Mon équipe (mes apporteurs)</SelectItem>}
                                                            <SelectItem value="custom">Personnes choisies ci-dessous</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                {/* Assigner à - multi */}
                                {(!form.is_fil_rouge || form.target_scope === 'custom') && (
                                <div className="space-y-2">
                                    <Label>Assigner à <span className="text-xs text-slate-400">(plusieurs possibles)</span></Label>
                                    <div className="border border-slate-200 rounded-md max-h-44 overflow-y-auto bg-white">
                                        <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100">
                                            <input type="checkbox" className="w-4 h-4 rounded text-blue-600" checked={form.assignee_ids.includes(user?.id)}
                                                onChange={(e) => setForm(p => ({ ...p, assignee_ids: e.target.checked ? Array.from(new Set([...(p.assignee_ids || []), user.id])) : (p.assignee_ids || []).filter(id => id !== user?.id) }))}
                                                data-testid="task-assignee-me" />
                                            <span className="text-sm">Moi-même</span>
                                        </label>
                                        {assignableUsers.filter(u => u.id !== user?.id).map(u => (
                                            <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                                                <input type="checkbox" className="w-4 h-4 rounded text-blue-600" checked={form.assignee_ids.includes(u.id)}
                                                    onChange={(e) => setForm(p => ({ ...p, assignee_ids: e.target.checked ? Array.from(new Set([...(p.assignee_ids || []), u.id])) : (p.assignee_ids || []).filter(id => id !== u.id) }))}
                                                    data-testid={`task-assignee-${u.id}`} />
                                                <span className="text-sm">{u.first_name} {u.last_name}</span>
                                                <span className="text-xs text-slate-400">({u.role === 'admin_full' || u.role === 'admin' ? 'Admin' : u.role === 'vp' ? 'VP' : 'Apporteur'})</span>
                                            </label>
                                        ))}
                                    </div>
                                    {form.assignee_ids.length === 0 && <p className="text-xs text-slate-500">Si personne n'est sélectionné, la tâche vous sera assignée.</p>}
                                </div>
                                )}
                                {isVP && !form.is_fil_rouge && (
                                    <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg">
                                        <input type="checkbox" id="is_team_task" checked={form.is_team_task} onChange={(e) => setForm(p => ({ ...p, is_team_task: e.target.checked }))} className="w-4 h-4 rounded text-violet-600" />
                                        <label htmlFor="is_team_task" className="text-sm"><span className="font-medium text-violet-700">Tâche d'équipe</span><span className="text-violet-500 ml-1">(visible par tous les membres)</span></label>
                                    </div>
                                )}
                                <Button type="submit" className="w-full" disabled={saving} data-testid="save-task-btn">
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    {editingTask ? 'Mettre à jour' : (form.is_fil_rouge ? 'Créer le fil rouge' : 'Créer la tâche')}
                                </Button>
                                {/* Actions rapides sur une tâche existante — visibles UNIQUEMENT en édition
                                    (deep-link Uatu `?edit=<id>` ou clic sur crayon). L'utilisateur peut :
                                    marquer terminée, prolonger, supprimer, sans quitter la modale. */}
                                {editingTask && (
                                    <div className="pt-2 mt-2 border-t border-slate-200">
                                        <p className="text-xs text-slate-500 mb-2">Actions rapides sur cette tâche</p>
                                        <div className="flex flex-wrap gap-2">
                                            {editingTask.status !== 'done' && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                                    data-testid="modal-complete-task-btn"
                                                    onClick={() => {
                                                        setDialogOpen(false);
                                                        setCompleteTask(editingTask);
                                                        setCompleteComment('');
                                                    }}
                                                >
                                                    <CheckCircle2 className="w-4 h-4 mr-1.5" />
                                                    Marquer terminée
                                                </Button>
                                            )}
                                            {editingTask.status !== 'done' && editingTask.status !== 'postponed' && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                                    data-testid="modal-postpone-task-btn"
                                                    onClick={() => {
                                                        setDialogOpen(false);
                                                        setPostponeTask(editingTask);
                                                        setPostponeReason('');
                                                        setPostponeDate('');
                                                    }}
                                                >
                                                    <Clock className="w-4 h-4 mr-1.5" />
                                                    Prolonger
                                                </Button>
                                            )}
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="border-red-300 text-red-700 hover:bg-red-50 ml-auto"
                                                data-testid="modal-delete-task-btn"
                                                onClick={async () => {
                                                    if (!window.confirm(`Supprimer la tâche « ${editingTask.title} » ?`)) return;
                                                    try {
                                                        await tasksAPI.delete(editingTask.id);
                                                        toast.success('Tâche supprimée');
                                                        setDialogOpen(false);
                                                        await loadData();
                                                    } catch (e) {
                                                        toast.error(e?.response?.data?.detail || 'Suppression impossible');
                                                    }
                                                }}
                                            >
                                                <Trash2 className="w-4 h-4 mr-1.5" />
                                                Supprimer
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Cartes de statut CLIQUABLES */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    {STATUS_CARDS.map(c => (
                        <button key={c.key} type="button" onClick={() => setFilterStatus(c.key)}
                            className={cn('text-left rounded-xl border transition-all', c.bg, filterStatus === c.key ? `ring-2 ${c.active} border-transparent` : 'border-slate-200 hover:border-slate-300')}
                            data-testid={`status-card-${c.key}`}>
                            <div className="p-4 flex items-center justify-between">
                                <div>
                                    <p className={cn('text-xs uppercase', c.text)}>{c.label}</p>
                                    <p className={cn('text-2xl font-bold', c.text)}>{c.value}</p>
                                </div>
                                <c.Icon className={cn('w-7 h-7 opacity-70', c.text)} />
                            </div>
                        </button>
                    ))}
                </div>

                {/* Filtres */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex flex-col lg:flex-row gap-3">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input placeholder="Rechercher (titre, dossier...)" className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="task-search" />
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {/* Période */}
                                <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                                    <SelectTrigger className="w-36" data-testid="filter-period"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toute période</SelectItem>
                                        <SelectItem value="today">Aujourd'hui</SelectItem>
                                        <SelectItem value="week">Cette semaine</SelectItem>
                                        <SelectItem value="month">Ce mois</SelectItem>
                                    </SelectContent>
                                </Select>
                                {/* Équipe (admin) */}
                                {isAdmin && teams.length > 0 && (
                                    <Select value={filterTeam || 'all'} onValueChange={(v) => setFilterTeam(v === 'all' ? '' : v)}>
                                        <SelectTrigger className="w-40" data-testid="filter-team"><SelectValue placeholder="Équipe" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Toutes équipes</SelectItem>
                                            {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                )}
                                {/* Utilisateur (admin) / Membre (VP) */}
                                {(isAdmin || isVP) && (
                                    <UserPickerPopover
                                        users={(isAdmin ? assignableUsers : [...teamMembers, ...assignableUsers]).filter((u, i, arr) => u.id !== user.id && arr.findIndex(x => x.id === u.id) === i)}
                                        value={filterUser || null}
                                        onChange={(id) => setFilterUser(id || '')}
                                        allowAll
                                        showAllLabel={isVP ? 'Toute mon équipe' : 'Tous les utilisateurs'}
                                        allowMe
                                        currentUserId={user.id}
                                        currentUserLabel={`Moi (${user.first_name})`}
                                        placeholder={isVP ? 'Mon équipe' : 'Utilisateur'}
                                        width={260}
                                        triggerClassName="w-44"
                                        testId="filter-user"
                                    />
                                )}
                                {/* Priorité */}
                                <Select value={filterPriority} onValueChange={setFilterPriority}>
                                    <SelectTrigger className="w-36" data-testid="filter-priority"><SelectValue placeholder="Priorité" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toutes priorités</SelectItem>
                                        <SelectItem value="urgent">Urgente</SelectItem>
                                        <SelectItem value="high">Haute</SelectItem>
                                        <SelectItem value="medium">Moyenne</SelectItem>
                                        <SelectItem value="low">Basse</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Barre de suppression multiple (Full Admin) */}
                {isAdminFull && selectedIds.size > 0 && (
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg" data-testid="bulk-delete-bar">
                        <span className="text-sm font-medium text-red-700">{selectedIds.size} tâche(s) sélectionnée(s)</span>
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Annuler</Button>
                            <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                                <Trash2 className="w-4 h-4 mr-1" />Supprimer la sélection
                            </Button>
                        </div>
                    </div>
                )}

                {/* Tableau de pilotage */}
                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[760px]" data-testid="tasks-table">
                                <thead>
                                    <tr className="text-left text-xs uppercase text-slate-500 border-b">
                                        {isAdminFull && (
                                            <th className="px-3 py-3 w-10">
                                                <input type="checkbox" className="w-4 h-4 rounded text-red-600 cursor-pointer"
                                                    checked={allDisplayedSelected} onChange={toggleSelectAll}
                                                    title="Tout sélectionner" data-testid="select-all-tasks" />
                                            </th>
                                        )}
                                        <th className="px-4 py-3 font-medium">Tâche</th>
                                        <th className="px-4 py-3 font-medium">Dossier</th>
                                        <th className="px-4 py-3 font-medium">Assigné(s)</th>
                                        <th className="px-4 py-3 font-medium">
                                            <button type="button" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                                                className="flex items-center gap-1 uppercase hover:text-slate-800 transition-colors" data-testid="sort-due-date">
                                                Échéance
                                                {sortDir === 'asc'
                                                    ? <ArrowUp className="w-3.5 h-3.5" />
                                                    : <ArrowDown className="w-3.5 h-3.5" />}
                                                <span className="normal-case text-[10px] text-slate-400">{sortDir === 'asc' ? '(la + proche)' : '(la + loin)'}</span>
                                            </button>
                                        </th>
                                        <th className="px-4 py-3 font-medium">Statut</th>
                                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedTasks.length === 0 ? (
                                        <tr><td colSpan={isAdminFull ? 7 : 6} className="px-4 py-12 text-center text-slate-400">Aucune tâche pour ces filtres</td></tr>
                                    ) : displayedTasks.map(task => (
                                        <TaskRow key={task.id} task={task} canRemind={isAdmin || isVP || task.created_by === user.id}
                                            selectable={isAdminFull} selected={selectedIds.has(task.id)} onToggleSelect={toggleSelect} highlight={flashTaskId === task.id}
                                            onStatusChange={handleStatusChange} onRemind={handleRemind} onEdit={openEdit} onDelete={handleDelete} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Dialog Prolongation : raison + nouvelle date plus lointaine (obligatoires) */}
            <Dialog open={!!postponeTask} onOpenChange={(o) => { if (!o) setPostponeTask(null); }}>
                <DialogContent className="w-[calc(100vw-2rem)] max-w-md overflow-x-auto sm:w-full" data-testid="postpone-dialog">
                    <DialogHeader>
                        <DialogTitle>Prolonger la tâche 📅</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-slate-500">
                            La tâche sortira des « en retard ». Indiquez une raison et une nouvelle date <strong>plus lointaine</strong>.
                        </p>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Raison du prolongement <span className="text-red-500">*</span></Label>
                            <Textarea value={postponeReason} onChange={(e) => setPostponeReason(e.target.value)}
                                placeholder="Ex : client absent, en attente d'un document…" rows={3}
                                data-testid="postpone-reason" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Nouvelle date d'échéance <span className="text-red-500">*</span></Label>
                            <Input type="date" value={postponeDate} onChange={(e) => setPostponeDate(e.target.value)}
                                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                                data-testid="postpone-date" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setPostponeTask(null)} data-testid="postpone-cancel">Annuler</Button>
                            <Button onClick={submitPostpone} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="postpone-confirm">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Prolonger'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog Clôture : compte-rendu obligatoire (publié sur le mur du dossier) */}
            <Dialog open={!!completeTask} onOpenChange={(o) => { if (!o) setCompleteTask(null); }}>
                <DialogContent className="w-[calc(100vw-2rem)] max-w-md overflow-x-auto sm:w-full" data-testid="complete-dialog">
                    <DialogHeader>
                        <DialogTitle>Terminer la tâche ✅</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-slate-500">
                            Décrivez <strong>ce qui a été fait</strong>. Ce compte-rendu sera publié automatiquement sur le <strong>mur du dossier</strong>, visible par toute l'équipe.
                        </p>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Compte-rendu <span className="text-red-500">*</span></Label>
                            <Textarea value={completeComment} onChange={(e) => setCompleteComment(e.target.value)}
                                placeholder="Ex : RDV réalisé, devis envoyé, client rappelé et relancé sous 8 jours…" rows={3}
                                data-testid="complete-comment" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setCompleteTask(null)} data-testid="complete-cancel">Annuler</Button>
                            <Button onClick={submitComplete} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="complete-confirm">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Terminer la tâche'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <NewTaskDialog
                open={newTaskDialogOpen}
                onOpenChange={setNewTaskDialogOpen}
                onCreated={() => { invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData(); }}
            />
        </DashboardLayout>
    );
}

function TaskRow({ task, canRemind, selectable, selected, onToggleSelect, onStatusChange, onRemind, onEdit, onDelete, highlight }) {
    const navigate = useNavigate();
    const overdue = isOverdue(task.due_date, task.status, task.is_overdue);
    const prio = getPriorityConfig(task.priority);
    const names = (task.assignee_names && task.assignee_names.length > 0) ? task.assignee_names : (task.assigned_to_name ? [task.assigned_to_name] : []);
    return (
        <tr className={cn('border-b last:border-0 hover:bg-slate-50/60 transition-all', overdue && 'bg-red-50/40', selected && 'bg-red-50/70', highlight && 'ring-2 ring-inset ring-indigo-400 bg-indigo-50/70 animate-pulse')} data-testid={`task-row-${task.id}`}>
            {selectable && (
                <td className="px-3 py-3 align-top">
                    <input type="checkbox" className="w-4 h-4 rounded text-red-600 cursor-pointer mt-1"
                        checked={!!selected} onChange={() => onToggleSelect(task.id)} data-testid={`select-task-${task.id}`} />
                </td>
            )}
            <td className="px-4 py-3 align-top">
                <div className="font-medium text-slate-900">{task.title}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge className={cn('text-[10px]', prio.color)}><prio.Icon className="w-3 h-3 mr-1" />{prio.label}</Badge>
                    {task.is_fil_rouge && (
                        <Badge className="text-[10px] whitespace-normal bg-rose-100 text-rose-700 hover:bg-rose-100" data-testid={`task-fil-rouge-badge-${task.id}`}>
                            🧵 Fil rouge · {filRougeRhythm(task)}{task.fil_rouge_day_index ? ` · ${task.fil_rouge_day_index}/${task.fil_rouge_total_occurrences ?? task.fil_rouge_total_days}` : ''}
                        </Badge>
                    )}
                    {task.module && <span className="text-[10px] text-slate-400">{task.module === 'both' ? 'Tout le dossier' : (task.module === 'chantier' ? 'Travaux d’optimisation' : 'Courtage')}</span>}
                </div>
            </td>
            <td className="px-4 py-3 align-top">
                {task.category === 'interne'
                    ? <span className="flex items-center gap-1 text-violet-600"><Tag className="w-3 h-3" />{task.theme || 'Interne'}</span>
                    : (task.prospect_id
                        // Un prospect_id existe → toujours afficher un lien cliquable, même si
                        // le nom n'a pas pu être résolu (fallback "Dossier lié") ou si le dossier
                        // est en corbeille (badge visuel pour prévenir l'utilisateur).
                        ? <button
                            type="button"
                            onClick={() => navigate(`/prospects/${task.prospect_id}`)}
                            className={cn(
                                "flex items-center gap-1 hover:underline text-left font-medium transition-colors",
                                task.prospect_is_deleted ? "text-slate-500 hover:text-slate-700" : "text-blue-600 hover:text-blue-800"
                            )}
                            title={task.prospect_is_deleted ? "Ouvrir le dossier (en corbeille)" : "Ouvrir le dossier"}
                            data-testid={`task-dossier-link-${task.id}`}
                          >
                            <Tag className="w-3 h-3 shrink-0" />
                            <span className="truncate max-w-[220px]">{task.prospect_name || 'Dossier lié'}</span>
                            {task.prospect_is_deleted && <span className="text-[9px] uppercase text-amber-600 ml-1">corbeille</span>}
                          </button>
                        : <span className="text-slate-300">—</span>)}
            </td>
            <td className="px-4 py-3 align-top">
                {names.length > 0 ? <span className="flex items-center gap-1 text-slate-700" title={names.join(', ')}><User className="w-3 h-3" />{names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`}</span> : <span className="text-slate-300">—</span>}
            </td>
            <td className="px-4 py-3 align-top">
                {task.due_date ? (
                    <div className="flex flex-col gap-1">
                        <span className={cn('flex items-center gap-1', overdue && 'text-red-600 font-medium')}><Calendar className="w-3 h-3" />{formatDate(task.due_date)}</span>
                        {overdue && (
                            <Badge className="w-fit text-[10px] bg-red-100 text-red-700 hover:bg-red-100 gap-1" data-testid={`task-overdue-${task.id}`}>
                                <AlertTriangle className="w-3 h-3" />{daysOverdue(task.due_date, task.status) >= 1 ? `${daysOverdue(task.due_date, task.status)} j de retard` : 'En retard'}
                            </Badge>
                        )}
                    </div>
                ) : <span className="text-slate-300">—</span>}
            </td>
            <td className="px-4 py-3 align-top">
                <Select value={task.status === 'in_progress' ? 'todo' : task.status} onValueChange={(v) => onStatusChange(task.id, v)}>
                    <SelectTrigger className="h-8 w-32" data-testid={`task-status-${task.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todo">À faire</SelectItem>
                        <SelectItem value="postponed">Prolongée 📅</SelectItem>
                        <SelectItem value="done">Terminée</SelectItem>
                    </SelectContent>
                </Select>
                {task.status === 'postponed' && task.postpone_reason && (
                    <p className="mt-1 text-[11px] text-amber-700 max-w-[160px] truncate" title={task.postpone_reason}>↳ {task.postpone_reason}</p>
                )}
            </td>
            <td className="px-4 py-3 align-top">
                <div className="flex items-center justify-end gap-1">
                    {canRemind && task.status !== 'done' && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700" title="Relancer (email + notif)" onClick={() => onRemind(task.id)} data-testid={`task-remind-${task.id}`}>
                            <Bell className="w-4 h-4" />
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Modifier" onClick={() => onEdit(task)} data-testid={`task-edit-${task.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600" title="Supprimer" onClick={() => onDelete(task.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
            </td>
        </tr>
    );
}
